// Attachment chip (Story #4613, Stream 8a) — thumbnail + name + size + progress
// + ✕ + per-file Retry, matching mockup 01's .filechip. Retry always re-stages
// (handled in useSupportAttachments), so the button here just calls onRetry.

import { FileText, Film, Image as ImageIcon, RotateCcw, X } from 'lucide-react'
import type { LocalAttachment } from '../../hooks/useSupportAttachments'
import { IMAGE_TYPES, VIDEO_TYPES } from '../../constants/support'
import { formatBytes } from '../../utils/supportDisplay'

function ThumbIcon({ contentType }: { contentType: string }) {
  if (IMAGE_TYPES.includes(contentType)) return <ImageIcon size={16} aria-hidden />
  if (VIDEO_TYPES.includes(contentType)) return <Film size={16} aria-hidden />
  return <FileText size={16} aria-hidden />
}

function statusLine(att: LocalAttachment): string {
  const size = formatBytes(att.sizeBytes)
  switch (att.status) {
    case 'done':
      return `${size} · uploaded ✓`
    case 'uploading':
      return `${size} · uploading… ${Math.round(att.progress * 100)}%`
    case 'binding':
      return `${size} · finishing…`
    case 'staging':
      return `${size} · preparing…`
    case 'error':
      return att.error || 'Upload failed'
    default:
      return size
  }
}

interface AttachmentChipProps {
  attachment: LocalAttachment
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
}

export default function AttachmentChip({ attachment, onRemove, onRetry }: AttachmentChipProps) {
  const isError = attachment.status === 'error'
  const showRetry = isError && !attachment.restored
  return (
    <div
      className={`mt-2 flex items-center gap-2.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] ${
        isError ? 'border-rose-200 bg-rose-50' : 'border-iflow-900/10 bg-white'
      }`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-iflow-100 text-iflow-700">
        <ThumbIcon contentType={attachment.contentType} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-slate-800">{attachment.filename}</span>
        <span className={`block text-[11.5px] ${isError ? 'text-rose-700' : 'text-slate-500'}`}>
          {statusLine(attachment)}
        </span>
        {attachment.status === 'uploading' && (
          <span className="mt-1 block h-1 overflow-hidden rounded bg-iflow-100">
            <span
              className="block h-full bg-iflow-500 transition-[width]"
              style={{ width: `${Math.round(attachment.progress * 100)}%` }}
            />
          </span>
        )}
      </span>
      {showRetry && (
        <button
          type="button"
          onClick={() => onRetry(attachment.localId)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11.5px] font-medium text-iflow-700 hover:bg-iflow-50"
          aria-label={`Retry ${attachment.filename}`}
        >
          <RotateCcw size={13} aria-hidden /> Retry
        </button>
      )}
      <button
        type="button"
        onClick={() => onRemove(attachment.localId)}
        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        aria-label={`Remove ${attachment.filename}`}
      >
        <X size={15} />
      </button>
    </div>
  )
}
